import React from "react";
import {resetApp} from "../reducers";
import {emulateUser$, login$, logout$, verifyAuth$} from "../services/common";
import {getMyCompanyJoinRequests$} from "../services/users";
import {createCompany$, requestToJoinCompany$} from "../services/companies";
import {getMyProfile} from "./users";
import moment from "moment";
import * as R from 'ramda';
import {reportError$} from "../services/ui";
import {reduxLogger} from "../index";
import * as ReactDOM from "react-dom";
import {createBid$, getBids$} from "../services/bids";
import {getBidDone} from "./bids";

export const NETWORK_ERROR = 'NETWORK_ERROR';
export const UI_ERROR = 'UI_ERROR';
export const UI_SUCCESS = 'UI_SUCCESS';
export const UI_INFO = 'UI_INFO';
export const SET_LOADING_FLAG = 'SET_LOADING_FLAG';
export const CLEAR_LOADING_FLAG = 'CLEAR_LOADING_FLAG';
export const SET_LOADING_ERROR = 'SET_LOADING_ERROR';
export const CLEAR_LOADING_ERROR = 'CLEAR_LOADING_ERROR';
export const SET_AUTHENTICATED_USER = 'SET_AUTHENTICATED_USER';
export const SET_EMULATED_USER = 'SET_EMULATED_USER';
export const CLEAR_EMULATED_USER = 'CLEAR_EMULATED_USER';
export const SET_AUTH_DATA = 'SET_AUTH_DATA';
export const SET_AUTH_STATE = 'SET_AUTH_STATE';
export const START_AUTH_POLL = 'START_AUTH_POLL';
export const STOP_AUTH_POLL = 'STOP_AUTH_POLL';

export const startAuthPoll = () => ({ type: START_AUTH_POLL, payload: {}});
export const stopAuthPoll = () => ({ type: STOP_AUTH_POLL, payload: {}});

const reportError = (error, location, state, timestamp) => async (dispatch, getState) => {
  try {
    const {response, status} = await reportError$({error, location, state, timestamp}).toPromise();
  } catch (error) {
    console.log(error);
  }
};

export const networkError = (error, location) => async (dispatch, getState) => {
  let payload;
  if (typeof error === 'string') {
    payload = {
      message: error,
      error: null,
      location,
    };
  } else {
    if (error.number) {
      const path = window.location.pathname;
      const state = R.assoc('_actions', reduxLogger.retrieve(), getState());
      const timestamp = moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      const uploadReport = () => dispatch(reportError(error, path, state, timestamp));

      let mailto = "mailto:support@cproject.com" +
        `?subject=${encodeURIComponent(`cProject Error #${error.number}`)}` +
        `&body=Error Code: #${error.number}%0D%0A` +
        `Timestamp: ${timestamp}%0D%0A%0D%0A` +
        "To help us identify the problem, please describe what you were doing when the error occurred: %0D%0A%0D%0A";

      payload = {
        message: (<React.Fragment><span>API error <strong>#{error.number}</strong> occurred.</span>
        <div style={{ marginTop: 6 }}>Please <a href={mailto} onClick={uploadReport}>report this error</a> to cProject support<br/>along with
          what you were doing at the time.
        </div>
      </React.Fragment>),
        error,
        location
      }
    } else {
      payload = {
        message: error.message,
        error,
        location
      };
    }
  }
  const path = window.location.pathname;
  const state = R.assoc('_actions', reduxLogger.retrieve(), getState());
  const timestamp = moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
  dispatch(reportError(error, path, state, timestamp));
  dispatch({
    type: NETWORK_ERROR,
    payload,
    error: true
  });
};

export const uiError = (error, location) => {
  let payload;
  console.log('uiError', error, location);
  if (typeof error === 'string') {
    payload = {
      message: error,
      error: null,
      location
    };
  } else {
    if (React.isValidElement(error)) {
      payload = {
        message: error,
        error: error.innerText,
        location
      };
    } else {
      payload = {
        message: error.name,
        error,
        location
      };
    }
  }
  return {
    type: UI_ERROR,
    payload,
    error: true
  };
};

export const uiSuccess = (content, location) => {
  let payload;
  if (typeof content === 'string') {
    payload = {
      message: content,
      content: null,
      location
    };
  } else {
    if (React.isValidElement(content)) {
      payload = {
        message: content,
        content: content.innerText,
        location
      };
    } else {
      payload = {
        message: content.name,
        content,
        location
      };
    }
  }
  return {
    type: UI_SUCCESS,
    payload,
    error: true
  };
};

export const uiInfo = (content, location) => {
  let payload;
  if (typeof content === 'string') {
    payload = {
      message: content,
      content: null,
      location
    };
  } else {
    if (React.isValidElement(content)) {
      payload = {
        message: content,
        content: content.innerText,
        location
      };
    } else {
      payload = {
        message: content.name,
        content,
        location
      };
    }
  }
  return {
    type: UI_INFO,
    payload,
    error: true
  };
};

export const setLoadingFlag = (flag) => ({type: SET_LOADING_FLAG, payload: {flag}});
export const clearLoadingFlag = (flag) => ({type: CLEAR_LOADING_FLAG, payload: {flag}});
export const setLoadingError = (flag, error) => ({type: SET_LOADING_ERROR, payload: {flag, error}});
export const setAuthState = (authState) => ({type: SET_AUTH_STATE, payload: { authState }});
export const setAuthData = (data) => ({ type: SET_AUTH_DATA, payload: { data }});

export const authStateChange = (authState, authData) => async (dispatch, getState) => {
  const conditionallyStartPoller = () => {
    if (getState().view.path && getState().view.path.includes('registration')) return;
    dispatch(startAuthPoll());
  };

  const hideMessage = authState === getState().ui.authState;

  if (authData) dispatch(setAuthData(authData));
  dispatch(setAuthState(authState));
  switch (authState) {
    case 'signedIn':
      dispatch(getMyProfile());
      conditionallyStartPoller();
      break;
    case 'registrationInProgress':
    case 'pendingCompanyAccept':
      conditionallyStartPoller();
      break;
    case 'signedOut':
      !hideMessage && dispatch(uiInfo('You have been signed out'));
      dispatch(stopAuthPoll());
      dispatch(resetApp());
      break;
    default:
      break;
  }
};

export const login = (username, password) => async (dispatch, getState) => {
  try {
    const { response, status } = await login$(username, password).toPromise();
    if (status === 200) {
      dispatch(authPoll());
    } else {
      dispatch(uiInfo('Unknown response from login request'));
    }
  } catch (error) {
    if ([400, 401].includes(error.status)) {
      dispatch(uiError('Incorrect user name or password'));
      console.log(error);
    } else {
      dispatch(uiError('Login failed'));
      console.log(error);
    }
  }
};

export const logout = () => async (dispatch, getState) => {
  await logout$().toPromise();
  dispatch(stopAuthPoll());
  dispatch(authStateChange('signedOut', {}));
};

export const emulateUser = (userId) => async (dispatch, getState) => {
  try {
    let { response, status } = await emulateUser$({ id: userId }).toPromise();
    document.location.reload();
  } catch (error) {
    dispatch(networkError({...error, number: '10007-'+error.status}));
  }
};

export const authPoll = () => async (dispatch, getState) => {
  try {
    let { response: authState, status } = await verifyAuth$().toPromise();
    switch (status) {
      case 200:
        dispatch(authStateChange('signedIn', authState));
        break;
      case 207:
        if (Object.keys(authState['403'])[0] === 'unauthorized') dispatch(authStateChange('beginUserRegistration', authState['200']));
        else {
          let { response: companyJoinReq, status: status2} = await getMyCompanyJoinRequests$({}).toPromise();
          switch (status2) {
            case 204:
              dispatch(authStateChange('registrationInProgress', authState['200']));
              break;
            case 200:
              dispatch(authStateChange('pendingCompanyAccept', { companyJoinReq, ...authState['200'] }));
              break;
            default:
              dispatch(authStateChange('registrationInProgress', authState['200']));
              break;
          }
        }
        break;
      default:
        dispatch(authStateChange('signedOut'));
        break;
    }
  } catch (error) {
    if (error.status === 401) {
      dispatch(authStateChange('signedOut'));
    }
  }
};

export const createCompany = (company) => async (dispatch, getState) => {
  try {
    await createCompany$({company}).toPromise();
    const response = await verifyAuth$().toPromise();
    if (response.status === 200) {
      dispatch(authStateChange('signedIn', response.response));
    } else if (response.status === 207) {
      dispatch(authStateChange('registrationInProgress', response.response['200']));
    } else {
      dispatch(authStateChange('signedOut'));
    }
  } catch (error) {
    dispatch(uiError(error, 'An error occurred while creating your company'));
  }
};

export const joinCompany = (company) => async (dispatch, getState) => {
  try {
    await requestToJoinCompany$({ companyId: company.id }).toPromise();
    await getMyCompanyJoinRequests$({}).toPromise();
    dispatch(authPoll());
  } catch (error) {
    dispatch(networkError({...error, number: '10061-'+error.status}));
  }
};



// WEBPACK FOOTER //
// ./src/actions/ui.js